import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { StatusBadge } from "../components/Brand";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";

export default function EngagementDetail() {
  const { engagementId } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [e, setE] = useState(null);
  const [poForm, setPoForm] = useState(null);

  useEffect(() => {
    api.get(`/engagements/${engagementId}`).then((r) => {
      setE(r.data);
      setPoForm({ po_number: r.data.po_number || "", po_required: !!r.data.po_required });
    }).catch((err) => toast.error(apiError(err)));
  }, [engagementId]);

  const savePo = async () => {
    try {
      const { data } = await api.patch(`/engagements/${engagementId}`, poForm);
      setE({ ...e, ...data });
      toast.success("Purchase order settings saved.");
    } catch (err) { toast.error(apiError(err)); }
  };

  if (!e) return <div className="text-sm text-[#78716C] animate-pulse" data-testid="engagement-detail-loading">Loading engagement…</div>;

  return (
    <div className="max-w-5xl space-y-6" data-testid="engagement-detail-page">
      <div className="fade-up">
        <Link to="/engagements" className="text-sm font-bold text-[#78716C] hover:text-navy flex items-center gap-1 mb-3" data-testid="back-to-engagements-link">
          <ArrowLeft size={14} /> Engagements</Link>
        <p className="eyebrow">Service engagement</p>
        <h1 className="section-title text-3xl mt-1">{e.title}</h1>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 fade-up-1" data-testid="engagement-meta-grid">
        {[["Organization", e.organization?.name], ["Dates", `${e.start_date} → ${e.end_date}`],
          ["Consultants", e.consultant_names.filter(Boolean).join(", ") || "—"],
          ["Days delivered", `${e.service_days.filter((d) => d.status === "completed").length} / ${e.planned_days} planned`]].map(([k, v]) => (
          <div key={k} className="card p-4"><div className="label !mb-1">{k}</div>
            <div className="text-sm font-bold text-navy">{v}</div></div>
        ))}
      </div>

      {isAdmin && poForm && (
        <div className="card p-5 fade-up-2" data-testid="engagement-po-card">
          <h2 className="font-serif font-bold text-lg text-navy mb-3">Purchase order requirements (Admin)</h2>
          <div className="flex flex-wrap items-end gap-4">
            <div><label className="label">Purchase Order number</label>
              <input className="input w-56" value={poForm.po_number} placeholder="None on file" data-testid="engagement-po-input"
                onChange={(ev) => setPoForm({ ...poForm, po_number: ev.target.value })} /></div>
            <label className="flex items-center gap-2 text-sm font-bold text-navy pb-2.5" data-testid="engagement-po-required-label">
              <input type="checkbox" className="accent-[#B85C37] w-4 h-4" checked={poForm.po_required}
                data-testid="engagement-po-required" onChange={(ev) => setPoForm({ ...poForm, po_required: ev.target.checked })} />
              Purchase Order required on invoices for this engagement</label>
            <button className="btn-outline !py-1.5" onClick={savePo} data-testid="engagement-po-save">Save</button>
          </div>
          <p className="text-xs text-[#78716C] mt-2">The Purchase Order number prefills invoice drafts for this engagement; a required Purchase Order is flagged on the invoice before submission.</p>
        </div>
      )}

      {e.notes && <div className="card p-5 text-sm text-[#57534E] fade-up-2" data-testid="engagement-notes">{e.notes}</div>}

      <div className="card p-6 fade-up-2" data-testid="engagement-service-days">
        <h2 className="font-serif font-bold text-lg text-navy mb-4">Service days</h2>
        <table className="w-full">
          <thead><tr><th className="table-th">Date</th><th className="table-th">School</th>
            <th className="table-th">Consultant</th><th className="table-th">Type</th><th className="table-th">Status</th></tr></thead>
          <tbody>
            {e.service_days.map((d) => (
              <tr key={d.id} className="hover:bg-canvas" data-testid={`service-day-row-${d.id}`}>
                <td className="table-td font-bold">{d.date}</td><td className="table-td">{d.school_name}</td>
                <td className="table-td">{d.consultant_name}</td><td className="table-td">{d.service_type}</td>
                <td className="table-td"><StatusBadge status={d.status} /></td>
              </tr>
            ))}
            {e.service_days.length === 0 && <tr><td colSpan={5} className="table-td text-center text-[#78716C] py-8">No service days scheduled.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="card p-6 fade-up-3" data-testid="engagement-logs">
        <h2 className="font-serif font-bold text-lg text-navy mb-4">Daily logs</h2>
        <ul className="divide-y divide-[#F3EFE6]">
          {e.logs.map((lg) => (
            <li key={lg.id} className="py-3 flex items-center justify-between gap-3" data-testid={`engagement-log-${lg.id}`}>
              <Link to={`/logs/${lg.id}`} className="text-sm font-bold text-navy hover:text-terracotta">
                {lg.date} · {lg.school_name} · {lg.consultant_name}</Link>
              <StatusBadge status={lg.status} />
            </li>
          ))}
          {e.logs.length === 0 && <li className="text-sm text-[#78716C] py-3">No logs yet.</li>}
        </ul>
      </div>
    </div>
  );
}
